import React, { useState, useEffect } from 'react';
import { Search, Plus, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusOptions = ['Processing', 'Ready for Pickup', 'Picked Up', 'Cancelled'];
const statusColors = { 'Picked Up': 'badge-delivered', Processing: 'badge-processing', 'Ready for Pickup': 'badge-shipped', Cancelled: 'badge-cancelled' };

const paymentStatusOptions = ['Pending Payment', 'Paid', 'Failed/Cancelled'];
const paymentStatusColors = {
  'Pending Payment': 'bg-amber-50 text-amber-700 border-amber-200',
  Paid: 'bg-green-50 text-green-700 border-green-200',
  'Failed/Cancelled': 'bg-red-50 text-red-700 border-red-200',
};

const emptyItem = { productName: '', size: '', price: 0, quantity: 1 };

const ManualOrderModal = ({ onClose, onCreated }) => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState({ customerName: '', phone: '', notes: '', orderType: 'pickup', deliveryAddress: '', deliveryCharge: 0, discount: 0 });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [saving, setSaving] = useState(false);

  const subtotal = items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0);
  const total = Math.max(0, subtotal - (Number(form.discount) || 0) + (Number(form.deliveryCharge) || 0));

  const updateItem = (idx, key, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));
  const addItem = () => setItems(prev => [...prev, { ...emptyItem }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.phone) { toast.error('Name and phone are required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customerName: form.customerName,
        phone: form.phone,
        notes: form.notes,
        orderType: form.orderType,
        deliveryAddress: form.deliveryAddress,
        items: items.filter(i => i.productName).map(i => ({
          productName: i.productName, size: i.size, price: Number(i.price) || 0, quantity: Number(i.quantity) || 1,
        })),
        subtotal,
        discount: Number(form.discount) || 0,
        deliveryCharge: Number(form.deliveryCharge) || 0,
        total,
      }, { headers: authHeader });
      toast.success('Order created');
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" data-testid="manual-order-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#ECE0DA]">
          <h2 className="font-playfair text-lg font-bold text-[#1F1410]">New Manual Order (Phone-in)</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#FDF8F3] rounded-full transition-colors" data-testid="manual-order-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <input value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))} placeholder="Customer name *" className="px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm" data-testid="manual-order-name" />
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone *" className="px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm" data-testid="manual-order-phone" />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-[#7A6A63] uppercase">Items</p>
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input value={it.productName} onChange={e => updateItem(idx, 'productName', e.target.value)} placeholder="Item name" className="flex-1 px-2 py-1.5 border border-[#ECE0DA] rounded-lg text-sm" data-testid={`manual-item-name-${idx}`} />
                <input value={it.size} onChange={e => updateItem(idx, 'size', e.target.value)} placeholder="Size" className="w-16 px-2 py-1.5 border border-[#ECE0DA] rounded-lg text-sm" data-testid={`manual-item-size-${idx}`} />
                <input type="number" value={it.price} onChange={e => updateItem(idx, 'price', e.target.value)} placeholder="₹" className="w-16 px-2 py-1.5 border border-[#ECE0DA] rounded-lg text-sm" data-testid={`manual-item-price-${idx}`} />
                <input type="number" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} placeholder="Qty" className="w-14 px-2 py-1.5 border border-[#ECE0DA] rounded-lg text-sm" data-testid={`manual-item-qty-${idx}`} />
                <button type="button" onClick={() => removeItem(idx)} className="text-[#7A6A63] hover:text-red-600" data-testid={`manual-item-remove-${idx}`}><X size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-xs font-semibold text-[#7A1125]" data-testid="manual-item-add">+ Add item</button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <select value={form.orderType} onChange={e => setForm(f => ({ ...f, orderType: e.target.value }))} className="px-2 py-2 border border-[#ECE0DA] rounded-lg text-sm" data-testid="manual-order-type">
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
            </select>
            <input type="number" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} placeholder="Discount ₹" className="px-2 py-2 border border-[#ECE0DA] rounded-lg text-sm" data-testid="manual-order-discount" />
            <input type="number" value={form.deliveryCharge} onChange={e => setForm(f => ({ ...f, deliveryCharge: e.target.value }))} placeholder="Delivery ₹" className="px-2 py-2 border border-[#ECE0DA] rounded-lg text-sm" data-testid="manual-order-delivery-charge" />
          </div>

          {form.orderType === 'delivery' && (
            <input value={form.deliveryAddress} onChange={e => setForm(f => ({ ...f, deliveryAddress: e.target.value }))} placeholder="Delivery address" className="w-full px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm" data-testid="manual-order-address" />
          )}

          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)" rows={2} className="w-full px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm resize-none" data-testid="manual-order-notes" />

          <div className="flex justify-between font-bold text-[#7A1125] text-sm border-t border-[#ECE0DA] pt-3">
            <span>Total</span><span data-testid="manual-order-total">₹{total}</span>
          </div>

          <button type="submit" disabled={saving} className="w-full bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold py-2.5 rounded-full text-sm transition-colors" data-testid="manual-order-submit">
            {saving ? 'Creating...' : 'Create Order'}
          </button>
        </form>
      </div>
    </div>
  );
};

const Orders = () => {
  const { authHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    const params = filterStatus ? `?status=${filterStatus}` : '';
    axios.get(`${API}/orders${params}`, { headers: authHeader })
      .then(r => setOrders(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filterStatus]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const { data } = await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, { headers: authHeader });
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status: data.status } : o));
      toast.success('Order status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handlePaymentStatusChange = async (orderId, newStatus) => {
    try {
      const { data } = await axios.put(`${API}/orders/${orderId}/payment-status`, { paymentStatus: newStatus }, { headers: authHeader });
      setOrders(prev => prev.map(o => o._id === orderId ? data : o));
      toast.success(newStatus === 'Paid' ? 'Marked as Paid — stock deducted' : 'Payment status updated');
    } catch (err) {
      toast.error('Failed to update payment status');
    }
  };

  const filtered = orders.filter(o =>
    !search || o.customerName?.toLowerCase().includes(search.toLowerCase()) || o.orderNumber?.includes(search)
  );

  return (
    <div className="p-6" data-testid="admin-orders">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Orders</h1>
          <p className="text-[#7A6A63] text-sm">{orders.length} total orders</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A63]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search orders..."
              className="pl-8 pr-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125] w-48"
              data-testid="orders-search"
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125] text-[#7A6A63]"
            data-testid="orders-filter-status"
          >
            <option value="">All Status</option>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={() => setManualOpen(true)}
            className="flex items-center gap-1.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            data-testid="new-manual-order-button"
          >
            <Plus size={14} /> New Order
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#ECE0DA] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#7A6A63]">No orders found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FDF8F3] border-b border-[#ECE0DA]">
                  {['Order #', 'Customer', 'Phone', 'Items', 'Total', 'Payment', 'Status', 'Date'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#7A6A63] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#ECE0DA]">
                {filtered.map(order => (
                  <tr key={order._id} className="hover:bg-[#FDF8F3] transition-colors" data-testid={`order-row-${order._id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-[#7A6A63]">{order.orderNumber}</td>
                    <td className="px-4 py-3 font-medium text-[#1F1410]">{order.customerName}</td>
                    <td className="px-4 py-3 text-[#7A6A63]">{order.phone}</td>
                    <td className="px-4 py-3 text-[#7A6A63] max-w-xs" data-testid={`order-items-${order._id}`}>
                      <span
                        className="line-clamp-2"
                        title={(order.items || []).map(i => `${i.productName}${i.size ? ` (${i.size})` : ''} x${i.quantity}`).join(', ')}
                      >
                        {(order.items || []).map(i => `${i.productName}${i.size ? ` (${i.size})` : ''} x${i.quantity}`).join(', ') || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#7A1125]">₹{order.total}</td>
                    <td className="px-4 py-3">
                      <select
                        value={order.paymentStatus || 'Pending Payment'}
                        onChange={e => handlePaymentStatusChange(order._id, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-semibold border cursor-pointer ${paymentStatusColors[order.paymentStatus] || paymentStatusColors['Pending Payment']}`}
                        data-testid={`order-payment-status-select-${order._id}`}
                      >
                        {paymentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={order.status}
                        onChange={e => handleStatusChange(order._id, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-semibold border cursor-pointer ${statusColors[order.status] || 'badge-processing'}`}
                        data-testid={`order-status-select-${order._id}`}
                      >
                        {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-[#7A6A63] text-xs">
                      {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {manualOpen && (
        <ManualOrderModal
          onClose={() => setManualOpen(false)}
          onCreated={(o) => { setOrders(prev => [o, ...prev]); setManualOpen(false); }}
        />
      )}
    </div>
  );
};

export default Orders;
